import { Dialog, P, Table, Td, Tr } from '@dnb/eufemia';
import { DialogContentProps, DialogProps } from '@dnb/eufemia/components/dialog/types';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { SubHeading } from '@dnb/eufemia/extensions/forms/Form';
import { CurrentApiAndScopeEntitlementsDto } from '@portals/shared/portal/AppApiEntitlementDto';

import style from './index.module.css';

import { UnselectableLabelStyle } from '../../../../index.styles';

type ScopeSelectionData = {
  selectedScopes: Record<string, boolean>;
};

type Props = {
  connectWithDialog: Pick<DialogProps & DialogContentProps, 'open' | 'onClose' | 'onConfirm' | 'onDecline'>;
  initalExtraScopes: string[];
  initialScopes: string[];
  formId: string;
  allEntitlements: CurrentApiAndScopeEntitlementsDto[];
  isLiveMode: boolean;
};

function updatedCheckboxLabel(label: string, initalScopes: string[], selectedScopes: Record<string, boolean>) {
  const isChecked = selectedScopes[label];
  const isInitial = initalScopes.includes(label);

  return (
    <P>
      {label}
      {isChecked && !isInitial ? <UnselectableLabelStyle> (added)</UnselectableLabelStyle> : ''}
      {!isChecked && isInitial ? <UnselectableLabelStyle> (removed)</UnselectableLabelStyle> : ''}
    </P>
  );
}

export default function SubmitConfirmationSummary({
  connectWithDialog,
  initalExtraScopes,
  initialScopes,
  allEntitlements,
  formId,
  isLiveMode,
}: Props) {
  const { data } = Form.getData<ScopeSelectionData>(formId);
  const selectedScopes = data?.selectedScopes ?? {};

  return (
    <Dialog
      description="The following access changes will be made to the client:"
      minWidth="auto"
      title="You are about to update the client accesses"
      variant="confirmation"
      {...connectWithDialog}
      alignContent="left"
      className={style['SubmitConfirmationSummary']}
      omitTriggerButton
    >
      <SubHeading bottom="small" top="small">
        Accesses
      </SubHeading>
      {allEntitlements
        .map((entitlement) => {
          const scopeEntitlementNames = entitlement.scopeEntitlements
            .filter((s) => s.liveMode === isLiveMode)
            .map((scopeEntitlement) => scopeEntitlement.scopeName);

          if (scopeEntitlementNames.length === 0) {
            return null;
          }

          return (
            <div key={entitlement.api.id}>
              <P size="small" weight="medium">
                {entitlement.api.name}
              </P>
              <Table outline={false} top="xx-small">
                <tbody>
                  <Tr key={entitlement.api.id}>
                    <Td style={{ paddingTop: '0' }}>
                      {scopeEntitlementNames.map((scopeName) => (
                        <Field.Toggle
                          disabled
                          key={scopeName}
                          label={updatedCheckboxLabel(scopeName, initialScopes, selectedScopes)}
                          path={`/selectedScopes/${scopeName}`}
                          valueOff={false}
                          valueOn
                          variant="checkbox"
                        />
                      ))}
                    </Td>
                  </Tr>
                </tbody>
              </Table>
            </div>
          );
        })
        .filter(Boolean)}
      {initalExtraScopes.length > 0 && (
        <>
          <P bottom="xx-small">Extra scopes</P>
          <Table>
            <tbody>
              <Tr key={1}>
                <Td style={{ paddingTop: '0' }}>
                  {initalExtraScopes.map((scope) => (
                    <Field.Toggle
                      disabled
                      key={scope}
                      label={updatedCheckboxLabel(scope, initalExtraScopes, selectedScopes)}
                      path={`/selectedScopes/${scope}`}
                      valueOff
                      valueOn
                      variant="checkbox"
                    />
                  ))}
                </Td>
              </Tr>
            </tbody>
          </Table>
        </>
      )}
    </Dialog>
  );
}
